import {
  Autocomplete,
  Chip,
  DialogContent,
  Grid2,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import React, { useState } from "react";
import validator from "validator";
import * as Yup from "yup";
import XCloseIco from "../../../../assets/icons/XCloseIco";
import CommonButton from "../../../../component/Button";
import CommonTextField from "../../../../component/Input";
import { CustomDiv } from "../../../chat-bot/style";
import {
  CommonGrid2,
  CustomTooltip,
} from "../../../common-components/StyledComponents";
import { CommonDialogStyle } from "../../../matches/quick-match/components/StyledComponent";

export default function ResumeUploadPortalLink({
  shareDialogOpen,
  handleShareDialogClose,
  handleShareResumeUpload,
  isLoading,
}) {
  const [copied, setCopied] = useState(false);
  const initialValues = {
    shareData: [],
    url: shareDialogOpen.data.link,
    max_resumes: "",
  };
  const [inputValue, setInputValue] = useState("");
  const handleShareDelete = (values, setFieldValue, option) => {
    const updatedValue = values.shareData.filter((item, i) => item !== option);
    setFieldValue("shareData", updatedValue);
  };

  const validationSchema = Yup.object({
    shareData: Yup.array()
      .of(
        Yup.string()
          .email("Invalid email address")
          .required("Email is required")
      )
      .test(
        "at-least-one-email",
        "At least one email is required",
        (value) => Array.isArray(value) && value.length > 0
      )
      .test("unique-emails", "Duplicate emails are not allowed", (value) => {
        if (!Array.isArray(value)) return true;
        const uniqueEmails = new Set(value);
        return value.length === uniqueEmails.size;
      }),
    max_resumes: Yup.string().required("Max Resume is required"),
  });

  const handleKeyDown = (e, setFieldValue, values) => {
    const inputValue = e.target.value.trim();

    if ((e.key === "Tab" || e.key === "Enter") && inputValue) {
      e.preventDefault();
      setInputValue("");

      if (validator.isEmail(inputValue)) {
        const newShareData = [...values.shareData, inputValue];
        setFieldValue("shareData", newShareData);
        e.target.value = "";
      }
    } else if (e.key === "Backspace" && e.target.value === "") {
      const updatedShareData = [...values.shareData];
      updatedShareData.pop();
      setFieldValue("shareData", updatedShareData);
    }
  };

  const handleCopyMouseLeave = () => {
    setTimeout(() => setCopied(false), 200);
  };

  const handleCopyClick = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (error) {}
  };

  const handleView = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <CommonDialogStyle
      maxWidth="md"
      fullWidth
      open={shareDialogOpen.open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleShareResumeUpload(values);
        }}
      >
        {({ values, setFieldValue, handleSubmit, errors, touched }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid2 container spacing={2}>
                  <Grid2 size={12}>
                    <CommonGrid2
                      display={"flex"}
                      justifyContent={"space-between"}
                      flexDirection={"row"}
                      gap={"10px"}
                      alignItems={"center"}
                    >
                      <Typography
                        variant="h3"
                        color={"#101828"}
                        fontWeight={600}
                      >
                        {"Resume Upload Portal Link"}
                      </Typography>
                      <IconButton onClick={handleShareDialogClose}>
                        <XCloseIco />
                      </IconButton>
                    </CommonGrid2>
                  </Grid2>

                  <Grid2 size={12}>
                    <Typography variant="h6" fontSize="15px">
                      Resume Upload Portal Link
                    </Typography>
                    <CommonGrid2
                      display={"flex"}
                      gap={"1rem"}
                      marginTop={"10px"}
                    >
                      <CommonTextField
                        padding={"10px"}
                        value={values.url}
                        disabled
                        customStyle={{
                          userSelect: "none",
                          "& .MuiInputBase-root": {
                            pointerEvents: "none",
                          },
                        }}
                      />
                      <CommonButton
                        value={"View"}
                        padding={"8px 1.5rem"}
                        variant="outlined"
                        fontWeight={"600"}
                        color="#344054"
                        border="#D0D5DD"
                        disabled={!shareDialogOpen.data_filled}
                        onClick={() => handleView(values.url)}
                      />
                      <CustomTooltip
                        arrow
                        placement="top"
                        title={copied ? "Copied!" : "Copy to Clipboard"}
                        fontSize="12px"
                        padding="5px 10px"
                        margin="0px 0px 10px !important"
                      >
                        <CustomDiv padding="0px" margin="0px">
                          <CommonButton
                            value={"Copy Link"}
                            padding={"8px 1.5rem"}
                            variant="outlined"
                            fontWeight={"600"}
                            color="#344054"
                            border="#D0D5DD"
                            disabled={!shareDialogOpen.data_filled}
                            onClick={() => handleCopyClick(values.url)}
                            onMouseLeave={handleCopyMouseLeave}
                          />
                        </CustomDiv>
                      </CustomTooltip>
                    </CommonGrid2>
                  </Grid2>
                  <Grid2
                    size={12}
                    display={"flex"}
                    flexDirection={"column"}
                    gap={"1.5rem"}
                  >
                    <Typography
                      variant="h3"
                      color={"#101828"}
                      fontWeight={600}
                      marginTop={"1rem"}
                    >
                      {"Share link"}
                    </Typography>
                    <Grid2 size={12}>
                      <Typography variant="h6" fontSize="15px">
                        {"Email Address"}
                      </Typography>

                      <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        limitTags={2}
                        value={values.shareData}
                        inputValue={inputValue}
                        onInputChange={(event, newInputValue) => {
                          setInputValue(newInputValue);
                        }}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={option}
                              {...getTagProps({ index })}
                              onDelete={() => {
                                handleShareDelete(
                                  values,
                                  setFieldValue,
                                  option
                                );
                              }}
                            />
                          ))
                        }
                        disabled={shareDialogOpen.data_filled}
                        renderInput={(params) => {
                          return (
                            <TextField
                              {...params}
                              variant="outlined"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: null,
                                onKeyDown: (e) =>
                                  handleKeyDown(e, setFieldValue, values),
                              }}
                              placeholder="Enter Email id"
                              error={
                                touched.shareData && Boolean(errors.shareData)
                              }
                              helperText={
                                touched.shareData && errors.shareData
                                  ? errors.shareData
                                  : ""
                              }
                            />
                          );
                        }}
                      />
                      <span style={{ fontSize: "10px" }}>
                        (Please type the next email ID and press the 'Enter'
                        button)
                      </span>
                    </Grid2>

                    <Grid2 size={12}>
                      <Typography
                        variant="h6"
                        marginBottom={"10px"}
                        fontSize="15px"
                      >
                        {"Max. number of resumes allowed per person?"}
                      </Typography>
                      <Grid2 size={6}>
                        <CommonTextField
                          padding={"10px"}
                          value={values.max_resumes}
                          width={"50%"}
                          disabled={shareDialogOpen.data_filled}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) {
                              setFieldValue("max_resumes", value);
                            }
                          }}
                          error={
                            touched.max_resumes && Boolean(errors.max_resumes)
                          }
                          helperText={
                            touched.max_resumes && errors.max_resumes
                              ? errors.max_resumes
                              : ""
                          }
                        />
                      </Grid2>
                    </Grid2>
                  </Grid2>
                  <Grid2
                    size={12}
                    display="flex"
                    textAlign={"left"}
                    justifyContent={"end"}
                    marginTop={"1rem"}
                  >
                    <CommonButton
                      fontWeight="600"
                      value="Share"
                      padding={"10px"}
                      width={"150px"}
                      type="submit"
                      disabled={isLoading || shareDialogOpen.data_filled}
                      loading={isLoading}
                    />
                  </Grid2>
                </Grid2>
              </DialogContent>
            </Form>
          );
        }}
      </Formik>
    </CommonDialogStyle>
  );
}
